import type { Except } from "type-fest";
import { z } from "zod";

import { bookableOptionValueZodSchema } from "../acquire/resource-reference-options/bookable-option-value-zod-schemas";
import { resourceReservationRequestZodSchema } from "../acquire/resources/resource-reservation-request";

import { isoAndUnixZodSchema, nowIsoAndUnix } from "../date-time/iso-and-unix";
import type { Time } from "../date-time/time";
import { timeZodSchema } from "../date-time/time";
import type { TimeSpot } from "../date-time/time-spot";
import { timeSpotZodSchema } from "../date-time/time-spot";
import { deliveryMethodType } from "../deliveries/delivery-methods";
import type { DeliveryZone } from "../deliveries/delivery-zone/delivery-zone";
import { deliveryZoneZodSchema } from "../deliveries/delivery-zone/delivery-zone";
import {
    shippingIdZodSchema,
    type ShippingId,
} from "../deliveries/shipping/shipping-id";
import { discountZodSchema } from "../discounts/discount";
import { orderItemZodSchema } from "../orders/order-items/order-item";
import type { Price } from "../prices/price";
import { priceZodSchema } from "../prices/price";
import { cart__productFormAnswerZodObject } from "../product-forms/cart.product-form-answer";
import { providerIdZodSchema } from "../providers/provider-id";
import { userAddressZodSchema, type UserAddress } from "../users/user-address";
import { cartIdFactory, cartIdZodSchema } from "./cart-id";

//Selected DeliverySelection
type PickUpDeliverySelection = {
    method: typeof deliveryMethodType.PICK_UP;
    slot?: TimeSpot;
    price: Price;
};

const pickupDeliverySelectionZodSchema: z.ZodSchema<PickUpDeliverySelection> =
    z.object({
        method: z.literal(deliveryMethodType.PICK_UP),
        slot: timeSpotZodSchema.optional(),
        price: priceZodSchema,
    });
// .transform(schema => ({ ...schema, slot: schema.slot || undefined }));

type HomeDeliverySelection = {
    method: typeof deliveryMethodType.HOME_DELIVERY;
    destination: UserAddress;
    shipping: ShippingId;
    slot?: Time;
    shippingTime: number; //DeliverySelectionTimeInMinutes
    zone: DeliveryZone;
    deliveryInstruction?: string;
    price: Price;
};

const homeDeliverySelectionZodSchema: z.ZodSchema<HomeDeliverySelection> =
    z.object({
        method: z.literal(deliveryMethodType.HOME_DELIVERY),
        destination: userAddressZodSchema,
        shipping: shippingIdZodSchema,
        slot: timeZodSchema.optional(),
        shippingTime: z.number(),
        zone: deliveryZoneZodSchema,
        deliveryInstruction: z.string().optional(),
        price: priceZodSchema,
    });
// .transform(schema => ({ ...schema, slot: schema.slot || undefined }));

type TableServiceDeliverySelection = {
    method: typeof deliveryMethodType.TABLE_SERVICE;
    notes?: string;
    price: Price;
    slot?: Time;
};
const tableServiceDeliverySelectionZodSchema: z.ZodSchema<TableServiceDeliverySelection> =
    z.object({
        method: z.literal(deliveryMethodType.TABLE_SERVICE),
        notes: z.string().optional(),
        slot: timeZodSchema.optional(),
        price: priceZodSchema,
    });
// .transform(schema => ({ ...schema, slot: schema.slot || undefined }));

type DigitalDeliverySelection = {
    method: typeof deliveryMethodType.DIGITAL;
    price: Price;
};

const digitalDeliverySelectionZodSchema: z.ZodSchema<DigitalDeliverySelection> =
    z.object({
        method: z.literal(deliveryMethodType.DIGITAL),
        price: priceZodSchema,
    });

type DeliverySelection =
    | PickUpDeliverySelection
    | HomeDeliverySelection
    | TableServiceDeliverySelection
    | DigitalDeliverySelection;

const deliverySelectionZodSchema = z.union([
    tableServiceDeliverySelectionZodSchema,
    digitalDeliverySelectionZodSchema,
    pickupDeliverySelectionZodSchema,
    homeDeliverySelectionZodSchema,
]);

const cartZodSchema = z.object({
    _id: cartIdZodSchema,
    _type: z.literal("CART"),
    _createdAt: isoAndUnixZodSchema,
    tip: z.number().int().min(0),
    price: priceZodSchema,
    discounts: z.array(discountZodSchema),
    orderItems: z.array(orderItemZodSchema),
    messageToProvider: z.string().nullish(),
    tipPrice: priceZodSchema,
    /**
     * NOTE! Doesn't include Service fee!
     * @type {Price}
     */
    totalPrice: priceZodSchema,
    hash: z.string(),
    delivery: deliverySelectionZodSchema, // todo: add schemas to delivery selections
    providerId: providerIdZodSchema,
    reservationRequests: z.array(resourceReservationRequestZodSchema),
    optionValues: z.array(bookableOptionValueZodSchema),
    preserve: z.boolean(),

    // newly added may 2024
    /**
     * Contains count of accommodation type and sub types
     * to be used for e.g. for SCB statistics. \
     * Format: { id: count, ... }
     */
    accommodationSubTypeCount: z
        .record(
            // accommodationTypeAndSubTypeIdZodSchema, Commented out since zod returns a partial record: https://github.com/colinhacks/zod/issues/2623
            z.number(),
        )
        .optional(),

    productFormAnswers: z
        .array(cart__productFormAnswerZodObject)
        // they are only optional since we cannot enforce them in all old clients at the same time
        .optional(),
});

type Cart_V1 = z.infer<typeof cartZodSchema>;

const _factoryParamsZodObject = cartZodSchema
    .omit({
        _id: true,
        _type: true,
        _createdAt: true,
        hash: true,
    })
    .merge(
        z.object({
            _id: cartIdZodSchema.optional(),
        }),
    );
type FactoryParams = z.infer<typeof _factoryParamsZodObject>;

function cartFactory(
    sourceCart: Cart_V1,
    hashFunc: (input: Except<Cart_V1, "hash">) => string,
): Cart_V1;
function cartFactory(
    params: FactoryParams,
    hashFunc: (input: Except<Cart_V1, "hash">) => string,
): Cart_V1;
function cartFactory(
    params: FactoryParams | Cart_V1,
    hashFunc: (input: Except<Cart_V1, "hash">) => string,
): Cart_V1 {
    const { hash: _hash, ..._params } =
        "hash" in params ? params : { ...params, hash: undefined };
    const almost: Except<Cart_V1, "hash"> = {
        ..._params,
        _type: "CART",
        _id: _params._id || cartIdFactory(),
        _createdAt:
            "_createdAt" in _params ? _params._createdAt : nowIsoAndUnix(),
    };

    const hashResult = hashFunc(almost);
    return {
        ...almost,
        hash: hashResult,
    };
}

export { cartFactory, cartZodSchema };
export type {
    Cart_V1 as Cart,
    DeliverySelection,
    DigitalDeliverySelection,
    HomeDeliverySelection,
    PickUpDeliverySelection,
    TableServiceDeliverySelection,
};
